import React, { useState } from 'react';
import {
  ArrowRight,
  Building2,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Code2,
  Link2,
  MapPin,
  Star,
} from 'lucide-react';
import { usePostHog } from 'posthog-js/react';
import { Place } from '../../../types/results.types';
import { DeduplicatedProof, groupProofByTag } from '../../../utils/websiteProof';

function highlightInSnippet(snippet: string, terms: string[]): React.ReactNode {
  if (!terms.length) return snippet;
  const escaped = terms.map(term => term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const pattern = new RegExp(`(${escaped.join('|')})`, 'gi');
  const parts = snippet.split(pattern);
  const lowerTerms = new Set(terms.map(term => term.toLowerCase()));
  return parts.map((part, index) =>
    lowerTerms.has(part.toLowerCase()) ? (
      <mark key={index} className="rounded-sm bg-yellow-100 px-0.5 font-semibold not-italic text-gray-900">
        {part}
      </mark>
    ) : part,
  );
}

export function getCategoryIcon(shortLabel: string): React.ReactElement {
  const label = shortLabel.toLowerCase();
  if (label.includes('seo')) return <MapPin className="h-3.5 w-3.5" />;
  if (label.includes('gbp') || label.includes('business profile')) return <Building2 className="h-3.5 w-3.5" />;
  if (label.includes('citation') || label.includes('nap')) return <Link2 className="h-3.5 w-3.5" />;
  if (label.includes('schema') || label.includes('on-page')) return <Code2 className="h-3.5 w-3.5" />;
  if (label.includes('review') || label.includes('reputation')) return <Star className="h-3.5 w-3.5" />;
  return <CheckCircle2 className="h-3.5 w-3.5" />;
}

const SectionCard: React.FC<{ title: string; children: React.ReactNode; aside?: React.ReactNode }> = ({ title, children, aside }) => (
  <section className="rounded-[22px] border border-[#EEE6E1] bg-white p-5 shadow-[0_10px_24px_rgba(45,49,66,0.04)]">
    <div className="mb-4 flex items-start justify-between gap-3">
      <h3 className="text-base font-bold tracking-tight text-[#2D3142]">{title}</h3>
      {aside}
    </div>
    {children}
  </section>
);

interface EvidenceArticleProps {
  tag: string;
  proofs: DeduplicatedProof[];
  allProofs: DeduplicatedProof[];
  shortLabel: string;
  place: Place;
  searchId?: number;
  compact?: boolean;
  isExpanded?: boolean;
  onToggle?: () => void;
}

const EvidenceArticle: React.FC<EvidenceArticleProps> = ({
  tag,
  proofs,
  allProofs,
  shortLabel,
  place,
  searchId,
  compact = false,
  isExpanded = false,
  onToggle,
}) => {
  const posthog = usePostHog();
  const hasMore = allProofs.length > proofs.length;
  const visible = isExpanded ? allProofs : proofs;

  return (
    <article className="rounded-[18px] border border-[#EEE6E1] bg-[#FFFEFD] p-4">
      <div className="mb-2 flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <span className="flex-shrink-0 text-emerald-500">{getCategoryIcon(shortLabel)}</span>
            <p className="text-sm font-bold text-[#2D3142]">{shortLabel}</p>
          </div>
          <p className="mt-1 text-[11px] text-[#7B7280]">
            {allProofs.length} snippet{allProofs.length === 1 ? '' : 's'} found
          </p>
        </div>
      </div>

      <div className="space-y-3">
        {visible.map((proof, index) => (
          <div key={`${proof.pageUrl}-${index}`} className={index > 0 ? 'border-t border-[#F1EBE7] pt-3' : ''}>
            <p className="text-sm italic leading-relaxed text-[#6B7280]">
              "{highlightInSnippet(proof.snippet, proof.matchedTerms)}"
            </p>
            <div className="mt-2 flex items-center justify-between gap-3">
              <span className="truncate text-xs text-[#7B7280]">{proof.pageDomain}</span>
              <a
                href={proof.pageUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex shrink-0 items-center gap-1 text-sm font-semibold text-[#8A3033] underline underline-offset-4"
                onClick={() => posthog?.capture('website_filter_evidence_source_clicked', {
                  search_id: searchId,
                  place_id: place.id,
                  tag,
                  page_url: proof.pageUrl,
                })}
              >
                View source
                <ArrowRight className="h-4 w-4" />
              </a>
            </div>
          </div>
        ))}
      </div>

      {!compact && hasMore && onToggle && (
        <button
          type="button"
          onClick={onToggle}
          className="mt-3 flex items-center gap-1 text-xs font-semibold text-emerald-700"
        >
          {isExpanded
            ? <><ChevronUp className="h-3 w-3" /> Show less</>
            : <><ChevronDown className="h-3 w-3" /> Show more</>}
        </button>
      )}
    </article>
  );
};

interface MatchEvidenceProps {
  place: Place;
  searchId?: number;
  shortLabels?: Record<string, string>;
}

function buildProofGroups(place: Place) {
  const fit = place.website_fit;
  const proofByTag = fit ? groupProofByTag(fit.evidence, fit.matched_offerings) : new Map<string, DeduplicatedProof[]>();
  const allProofByTag = fit ? groupProofByTag(fit.evidence, fit.matched_offerings, 99) : new Map<string, DeduplicatedProof[]>();
  return { fit, proofByTag, allProofByTag };
}

export function hasMatchEvidence(place: Place): boolean {
  const { proofByTag } = buildProofGroups(place);
  return Array.from(proofByTag.values()).some(proofs => proofs.length > 0);
}

export const MatchEvidencePreview: React.FC<MatchEvidenceProps & { onViewAll: () => void }> = ({
  place,
  searchId,
  shortLabels = {},
  onViewAll,
}) => {
  const { proofByTag, allProofByTag } = buildProofGroups(place);
  const firstEntry = Array.from(proofByTag.entries()).find(([, proofs]) => proofs.length > 0);

  if (!firstEntry) return null;

  const [tag, proofs] = firstEntry;
  const allProofs = allProofByTag.get(tag) ?? proofs;
  const shortLabel = shortLabels[tag] ?? tag;

  return (
    <SectionCard
      title="Match Evidence"
      aside={
        <button
          type="button"
          onClick={onViewAll}
          className="inline-flex items-center gap-1 text-sm font-semibold text-[#8A3033] underline underline-offset-4"
        >
          View all
          <ArrowRight className="h-4 w-4" />
        </button>
      }
    >
      <EvidenceArticle
        tag={tag}
        proofs={proofs.slice(0, 1)}
        allProofs={allProofs}
        shortLabel={shortLabel}
        place={place}
        searchId={searchId}
        compact
      />
    </SectionCard>
  );
};

const MatchEvidenceTab: React.FC<MatchEvidenceProps> = ({ place, searchId, shortLabels = {} }) => {
  const { proofByTag, allProofByTag } = buildProofGroups(place);
  const [expandedTags, setExpandedTags] = useState<Record<string, boolean>>({});
  const entries = Array.from(proofByTag.entries()).filter(([, proofs]) => proofs.length > 0);

  const toggleTag = (tag: string) => setExpandedTags(previous => ({ ...previous, [tag]: !previous[tag] }));

  if (entries.length === 0) {
    return (
      <SectionCard title="Match Evidence">
        <p className="text-sm text-[#6B7280]">No match evidence available for this company.</p>
      </SectionCard>
    );
  }

  return (
    <SectionCard title="Match Evidence">
      <div className="space-y-3">
        {entries.map(([tag, proofs]) => {
          const allProofs = allProofByTag.get(tag) ?? proofs;
          const shortLabel = shortLabels[tag] ?? tag;

          return (
            <EvidenceArticle
              key={tag}
              tag={tag}
              proofs={proofs}
              allProofs={allProofs}
              shortLabel={shortLabel}
              place={place}
              searchId={searchId}
              isExpanded={expandedTags[tag] ?? false}
              onToggle={() => toggleTag(tag)}
            />
          );
        })}
      </div>
    </SectionCard>
  );
};

export default MatchEvidenceTab;
